import { getFeature } from '../../core/artifacts/feature-utils';
// ---------------------------------
// Types
// ---------------------------------
import {
  isIndividualEvidence as _isIndividualEvidence,
  isCPT,
  isNgram,
  isIndividualEvidence,
  isCategorical
} from '../../typeguards/index';
import { FeatureTypes, SpanLevel } from '../../enums/index';
import { getCode } from './lookup';
import { uuidv4 } from './uuidv4';

// ---------------------------------
// Transformers and Filters
// ---------------------------------

const procedureCodeTransformer = (
  code: CMxAPI.ProcedureCode,
  fn: CMxArtifacts.DestructiveCodeTransformer
): CMxAPI.ProcedureCode => {
  fn(code);
  genericCodeTransformer(code, fn);
  return code;
};

const genericCodeTransformer = (
  code: any,
  fn: CMxArtifacts.DestructiveCodeTransformer
) => {
  if ('diagnoses' in code) {
    code.diagnoses?.codes?.forEach(fn);
  }
  if ('modifiers' in code) {
    code.modifiers?.codes?.forEach(fn);
  }
  return code;
};

const procedureEvidenceTransformer = (
  code: CMxAPI.MedicalCode,
  fn: CMxArtifacts.DestructiveEvidenceFunction
) => {
  code.evidence = code.evidence?.map(fn) ?? [];
  if (isCPT(code)) {
    genericEvidenceTransformer(code, fn);
  }
  return code;
};

const genericEvidenceTransformer = (
  code: any,
  fn: CMxArtifacts.DestructiveEvidenceFunction
) => {
  code.diagnoses?.codes?.forEach(
    (icd: CMxAPI.DiagnosisCode) => (icd.evidence = icd.evidence?.map(fn))
  );
  code.modifiers?.codes?.forEach(
    (mod: CMxAPI.ModifierCode) => (mod.evidence = mod.evidence?.map(fn))
  );
};

const genericEvidenceFilter = (
  code: any,
  fn: CMxArtifacts.EvidencePredicate
) => {
  code.diagnoses?.codes?.forEach((icd: CMxAPI.DiagnosisCode) => {
    icd.evidence = icd.evidence?.filter(fn);
  });
  code.modifiers?.codes?.forEach((mod: CMxAPI.ModifierCode) => {
    mod.evidence = mod.evidence?.filter(fn);
  });
};

const procedureEvidenceFilter = (
  code: CMxAPI.MedicalCode,
  fn: CMxArtifacts.EvidencePredicate
) => {
  const evidence = code.evidence?.filter(fn);
  code.evidence = evidence;
  if (isCPT(code)) {
    genericEvidenceFilter(code, fn);
  }
  return code;
};

const buildEvidenceTransformer = (
  fn: CMxArtifacts.DestructiveEvidenceFunction
) => {
  return (code: any) => {
    genericEvidenceTransformer(code, fn);
    return procedureEvidenceTransformer(code, fn);
  };
};

export const buildEvidenceFilter = (fn: CMxArtifacts.EvidencePredicate) => {
  return (code: any) => {
    procedureEvidenceFilter(code, fn);
    genericEvidenceFilter(code, fn);
    return code;
  };
};

export const buildCodeTransformer = (
  fn: CMxArtifacts.DestructiveCodeTransformer
) => {
  return (code: any) => {
    procedureCodeTransformer(code, fn);
    genericCodeTransformer(code, fn);
    return code;
  };
};

// unused for now.  uncomment if we do need them.

// const procedureCodeFilter = (
//   code: CMxAPI.ProcedureCode,
//   fn: MedicalCodePredicate
// ): boolean => {
//   return fn(code);
// };

// const genericCodeFilter = (code: any, fn: MedicalCodePredicate) => {
//   if ('diagnoses' in code) {
//     code.diagnoses.codes = code.diagnoses?.codes.filter(fn);
//   }
//   if ('modifiers' in code) {
//     code.modifiers.codes = code.modifiers?.codes.filter(fn);
//   }
//   return code;
// };

// ---------------------------------
// Individual transformers and filters
// ---------------------------------

const _onlySentences: CMxArtifacts.DestructiveEvidenceFunction = (
  piece: CMxAPI.UnionEvidence
) => {
  const feature = getFeature(piece);
  if (isNgram(feature)) {
    feature.spans =
      feature.spans?.filter(span => span.level === SpanLevel.SENTENCE) ?? [];
  }
  return piece;
};

const _uuidEvidence: CMxArtifacts.DestructiveEvidenceFunction = (
  piece: CMxAPI.UnionEvidence
) => {
  const feature = getFeature(piece);
  feature.uuid = uuidv4();
  if (_isIndividualEvidence(piece)) {
    if (isNgram(feature)) {
      const spans = feature.spans ?? [];
      spans.forEach(span => {
        span.uuid = uuidv4();
      });
    }
  }
  return piece;
};

const startRe = new RegExp(/^(\r\n|\n|\r)+/gm);
const endRe = new RegExp(/(\r\n|\n|\r)+$/gm);

const _normalizeTextContent: CMxArtifacts.DestructiveEvidenceFunction = (
  piece: CMxAPI.UnionEvidence
) => {
  const feature = getFeature(piece);
  if (_isIndividualEvidence(piece)) {
    if (isNgram(feature)) {
      const spans = feature.spans ?? [];
      spans.forEach((span: CMxAPI.Span) => {
        const { content } = span;
        const matchedStart = content.match(startRe);
        const matchedEnd = content.match(endRe);

        if (matchedStart?.length) {
          const [first] = matchedStart;
          span.content = span.content.replace(startRe, '');
          span.start += first.length;
        }

        if (matchedEnd?.length) {
          const [endTrim] = matchedEnd;
          span.content = span.content.replace(endRe, '');
          span.end -= endTrim.length;
        }
      });
    }
  }
  return piece;
};

const _uuidCodes = (code: CMxAPI.MedicalCode) => {
  code.uuid = uuidv4();
  return code;
};

const _uniqueSpans: CMxArtifacts.DestructiveEvidenceFunction = (
  piece: CMxAPI.UnionEvidence
) => {
  const feature = getFeature(piece);
  const seenFeatures = new Set<string>();

  const seenFilter = (span: CMxAPI.Span) => {
    const hash = `${span.start}-${span.end}`;
    if (seenFeatures.has(hash)) {
      return false;
    }
    seenFeatures.add(hash);
    return true;
  };

  if (isNgram(feature)) {
    feature.spans = feature.spans.filter(seenFilter);
  }
  return piece;
};

const _hasEvidence = (evidence: CMxAPI.UnionEvidence) => {
  const feature = getFeature(evidence);
  if (!isNgram(feature)) return true;

  return (feature.spans ?? []).length > 0;
};

const _normalizeTypeNames: CMxArtifacts.DestructiveEvidenceFunction = (
  evidence: CMxAPI.UnionEvidence
) => {
  evidence.type = (evidence.type ?? '').toLowerCase();
  return evidence;
};

const codeEvidenceMapper = (
  code: CMxAPI.MedicalCode,
  fn: (evidence: CMxAPI.UnionEvidence) => CMxAPI.UnionEvidence
) => {
  if (code.evidence?.length) {
    const evidence = code.evidence.map(fn);
    const flattened = evidence?.flat();
    // if (flattened.length !== code.evidence.length){
    //   console.log(flattened);
    // }
    return flattened;
  }
  return [];
};

const _splitFeatures = (code: CMxAPI.MedicalCode) => {
  code.evidence = codeEvidenceMapper(code, (evidence: CMxAPI.UnionEvidence) => {
    if (_isIndividualEvidence(evidence)) {
      let returnEvidence = evidence;
      const { feature } = evidence;
      if (isNgram(feature)) {
        const { spans = [] } = feature;
        //@ts-ignore
        returnEvidence = [...spans].map(span => {
          const base = { ...feature };
          base.spans = [span];
          return { ...evidence, feature: base };
        });
      }
      return returnEvidence;
    }
    return evidence;
  });
  return code;
};

const ngramHash = (span: CMxAPI.Span, code: CMxAPI.MedicalCode) => {
  return `${span.start}-${span.end}-${getCode(code)}`;
};

const _hashNgrams = (code: CMxAPI.MedicalCode) => {
  code.evidence = codeEvidenceMapper(code, (evidence: CMxAPI.UnionEvidence) => {
    if (_isIndividualEvidence(evidence)) {
      const { feature } = evidence;
      if (isNgram(feature)) {
        const { spans = [] } = feature;
        spans.forEach(span => {
          // hashing function
          span.hash = ngramHash(span, code);
        });
      }
    }
    return evidence;
  });
  return code;
};

type StringMapping = {
  [key: string]: string;
};

const mapping: StringMapping = {
  raw_reasonforvisit: 'reason_for_visit',
  exam_type: 'exam_description'
};

const categoricalTypes = new Set<string>(['exam_type', 'raw_reasonforvisit']);

const _convertToCategorical: CMxArtifacts.DestructiveEvidenceFunction = (
  evidence: CMxAPI.UnionEvidence
) => {
  if (_isIndividualEvidence(evidence)) {
    const feature = getFeature(evidence);
    if (categoricalTypes.has(feature.field)) {
      const fieldName = mapping[feature.field] || feature.field;

      const categorical = {
        type: 'categorical',
        score: evidence.score,
        feature: {
          uuid: uuidv4(),
          type: FeatureTypes.CATEGORICAL,
          field: fieldName,
          category: 'something'
        }
      };
      //console.log(`building categorical: ${fieldName} ${feature.field}`, categorical);
      return categorical;
    }
  }

  return evidence;
};

const dedupeFeatures = (code: CMxAPI.MedicalCode) => {
  //
  const seenFeatures = new Set<string>();
  const seenCategories = new Set<string>();

  const seenFeaturesFilter = (span: CMxAPI.Span) => {
    const hash = `${span.start}-${span.end}-${getCode(code)}-${code.uuid}`;
    if (seenFeatures.has(hash)) {
      return false;
    }
    seenFeatures.add(hash);
    return true;
  };

  const seenCategoryFilter = (evidence: CMxAPI.UnionEvidence) => {
    const feature = getFeature(evidence);
    if (isCategorical(feature)) {
      if (seenCategories.has(feature.field)) {
        return false;
      }
      seenCategories.add(feature.field);
    }
    return true;
  };

  code.evidence = code.evidence?.filter(seenCategoryFilter);

  code.evidence?.forEach((evidence: CMxAPI.UnionEvidence) => {
    if (isIndividualEvidence(evidence)) {
      const { feature } = evidence;
      if (isNgram(feature)) {
        feature.spans = feature.spans?.filter(seenFeaturesFilter) ?? [];
      }
    }
  });

  return code;
};

const deduper = buildCodeTransformer(dedupeFeatures);

const weedOutNonSentenceNgrams = buildEvidenceTransformer(_onlySentences);
const uniqueEvidenceSpans = buildEvidenceTransformer(_uniqueSpans);
const normalizeType = buildEvidenceTransformer(_normalizeTypeNames);
const uuidEvidence = buildEvidenceTransformer(_uuidEvidence);
const convertToCategorical = buildEvidenceTransformer(_convertToCategorical);
const normalizeTextContent = buildEvidenceTransformer(_normalizeTextContent);
const hashNgrams = buildCodeTransformer(_hashNgrams);

const hasEvidenceFilter = buildEvidenceFilter(_hasEvidence);
const onlyKnownEvidence = buildEvidenceFilter(_isIndividualEvidence);

const splitFeatures = buildCodeTransformer(_splitFeatures);
const uuidCodes = buildCodeTransformer(_uuidCodes);

export const transformers = {
  weedOutNonSentenceNgrams,
  uniqueEvidenceSpans,
  normalizeType,
  uuidEvidence,
  deduper,
  convertToCategorical,
  normalizeTextContent,
  hashNgrams,
  splitFeatures,
  uuidCodes
};

export const filters = {
  hasEvidenceFilter,
  onlyKnownEvidence
};
